import { CheckIcon, InformationCircleIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { FooterButtons } from "../../../common/components/FooterButtons";
import PlatformIcon from "../../../common/components/Icon/PlatformIcon/PlatformIcon";
import ManualInterventionIcon from "../../../common/components/Icon/ManualInterventionIcon";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import { useAppDispatch } from "../../../common/state/hooks";
import { getPlatformBuildInfo } from "../../../common/util/getPlatformBuildInfo";
import { prettifyPlatformName } from "../../../common/util/prettifyPlatformName";
import { IBuild } from "../../../model/model";
import {
  getLatestBuildsOfProjectGroup,
  getNewPublicationSettings,
  setSelectedPlatforms,
} from "../../builds/state/buildsSlice";
import { getSelectedProjectGroup } from "../../projectGroups/state/projectGroupsSlice";
import { platformGroups } from "./PlatformCards";

type Props = {
  incrementStep?(): void;
  decrementStep?(): void;
  isTheOnlyPublishingStep?: boolean;
};

export const SELECT_PLATFORMS_STEP_COMPONENT_ID = "selectPlatformsStepComponentId";

const mapPlatformName = (platformName: string): string => {
  if (platformName === "OculusQuest") return "MetaQuest";
  return platformName;
};

export default function SelectPlatformsStep({
  incrementStep,
  decrementStep,
  isTheOnlyPublishingStep = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const selectedProjectGroup = useSelector(getSelectedProjectGroup);
  const newPublicationSettings = useSelector(getNewPublicationSettings);
  const latestBuildsOfProjectGroup = useSelector(
    getLatestBuildsOfProjectGroup(selectedProjectGroup.id),
  );
  const selectedPlatforms = newPublicationSettings.selectedPlatforms || [];

  const isPlatformDisabled = (platformId: string) => {
    if (!isTheOnlyPublishingStep) return false;
    return Boolean(
      latestBuildsOfProjectGroup.find((b: IBuild) => {
        if (b?.scorm_version) return "WebGLApp_scorm" === platformId;
        return b.target_platform === platformId;
      }),
    );
  };

  const handleCardClick = (platformId: string) => {
    if (isPlatformDisabled(platformId)) return;
    const isSelected = selectedPlatforms.includes(platformId);
    const next = isSelected
      ? selectedPlatforms.filter((id: string) => id !== platformId)
      : [...selectedPlatforms, platformId];
    dispatch(setSelectedPlatforms(next));
  };

  const handleGroupClick = (platformIds: string[]) => {
    const selectableIds = platformIds.filter((id) => !isPlatformDisabled(id));
    const allSelected = selectableIds.every((id) => selectedPlatforms.includes(id));
    const next = allSelected
      ? selectedPlatforms.filter((id: string) => !selectableIds.includes(id))
      : Array.from(new Set([...selectedPlatforms, ...selectableIds]));
    dispatch(setSelectedPlatforms(next));
  };

  const navigateToPublishView = () => navigate(`/courses/${selectedProjectGroup.id}/publish`);

  const timeEstimateForPublication = selectedPlatforms.reduce((acc: number, platformId: string) => {
    const platformInfo = getPlatformBuildInfo(platformId);
    if (platformInfo) return acc + platformInfo.buildDurationInMin;
    return acc;
  }, 0);

  const hasContactNeededPlatform = selectedPlatforms.some(
    (id: string) => id === "iPhone" || id === "iPad" || id === "MacOS" || id === "WebGLApp_scorm",
  );

  return (
    <div className="flex flex-col grow w-full h-full justify-between">
      <IsLoading
        componentId={SELECT_PLATFORMS_STEP_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <div className="flex flex-col items-center justify-center flex-1 overflow-auto px-6 pt-2 pb-4">
          <p className="text-sm text-gray-400 mb-8">
            {t("pages.projectGroup.publish.multiSelectHint")}
          </p>

          <div className="relative flex flex-wrap items-start justify-center gap-x-1 gap-y-4 mb-16">
            {platformGroups.map((group, groupIdx) => (
              <div key={group.labelKey} className="flex items-start gap-x-1">
                {/* Group: label + cards */}
                <div className="flex flex-col items-center">
                  <p
                    onClick={() => handleGroupClick(group.platforms)}
                    className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2 cursor-pointer select-none rounded px-2 py-0.5 transition-colors hover:text-gray-600 hover:bg-gray-100"
                  >
                    {t(group.labelKey)}
                  </p>
                  <div className="flex gap-2">
                    {group.platforms.map((platformId) => {
                      const disabled = isPlatformDisabled(platformId);
                      const selected = selectedPlatforms.includes(platformId);
                      const isManualBuild = getPlatformBuildInfo(platformId)?.isManualBuild;
                      const displayName = mapPlatformName(platformId);

                      return (
                        <Tooltip
                          key={platformId}
                          className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
                          classNameChildren="left-0"
                          forceSingleLine={false}
                          message={
                            disabled
                              ? t("pages.projectGroup.publish.unselectablePlatformTooltip")
                              : ""
                          }
                        >
                          <div
                            id={`platform-card-${platformId}`}
                            onClick={() => handleCardClick(platformId)}
                            className={[
                              "relative flex flex-col items-center justify-center",
                              "w-[6.5rem] h-[5rem] rounded-lg border-2 select-none",
                              "transition-all duration-150 ease-in-out",
                              disabled
                                ? "opacity-40 cursor-not-allowed border-gray-200 bg-gray-50"
                                : selected
                                ? "border-green-500 bg-green-50 shadow"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm cursor-pointer",
                            ].join(" ")}
                          >
                            {selected && (
                              <div className="absolute -top-1.5 -left-1.5 w-[18px] h-[18px] bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                                <CheckIcon className="w-2.5 h-2.5 text-white stroke-[3]" />
                              </div>
                            )}
                            {isManualBuild && (
                              <div className="absolute top-0.5 right-0.5 w-4">
                                <Tooltip
                                  message={t(
                                    "pages.projectGroup.publish.manualInterventionTooltip",
                                  )}
                                  classNameChildren="w-64"
                                  forceSingleLine={false}
                                >
                                  <ManualInterventionIcon />
                                </Tooltip>
                              </div>
                            )}
                            <PlatformIcon
                              platform={platformId}
                              iconProps={{ width: 24, height: 24 }}
                            />
                            <p className="mt-1 font-semibold text-[11px] text-gray-700 text-center leading-tight">
                              {prettifyPlatformName(displayName)}
                            </p>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>

                {/* Vertical separator between groups */}
                {groupIdx < platformGroups.length - 1 && (
                  <div className="self-stretch flex items-center px-2 pt-5">
                    <div className="w-px h-full bg-gray-200" />
                  </div>
                )}
              </div>
            ))}

            {/* Red warning — absolute so it never shifts cards */}
            {hasContactNeededPlatform && (
              <div className="absolute top-full mt-8 left-1/2 -translate-x-1/2 w-full flex justify-center">
                <div className="flex items-start gap-2 text-red-600 max-w-3xl">
                  <InformationCircleIcon className="w-5 h-5 shrink-0 mt-0.5" />
                  <p className="text-sm leading-snug">
                    {t("pages.projectGroup.publish.contactNeededWarning")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Time estimate — always reserves space, content only visible when > 0 */}
        <p
          className={`text-sm font-semibold text-gray-500 text-right px-10 pb-1 ${
            timeEstimateForPublication > 0 ? "" : "invisible"
          }`}
        >
          {t("pages.projectGroup.publish.publishDuration")} {timeEstimateForPublication}{" "}
          {t("pages.projectGroup.publish.minutes")}
        </p>

        <FooterButtons
          cancelText={t("general.cancel")}
          cancelCb={() => navigateToPublishView()}
          backText={t("general.back")}
          backCb={decrementStep}
          nextText={t("general.next")}
          nextCb={() => incrementStep && incrementStep()}
          nextDisabled={!selectedPlatforms.length}
        />
      </IsLoading>
    </div>
  );
}
